import { Link } from "react-router-dom";
import MovieGrid from "@/components/feature/MovieGrid";
import SectionHeading from "@/components/feature/SectionHeading";
import { useFavorites } from "@/hooks/useFavorites";
import { useSeo } from "@/hooks/useSeo";
import { getHotMovies, getMovieById } from "@/lib/movies";
import type { Movie } from "@/types/movie";

export default function Favorites() {
  const { favorites } = useFavorites();
  useSeo({
    title: "我的收藏 - 免费看大片",
    description: "查看已在免费看大片收藏的影片，收藏记录保存在本机浏览器，随时回到你的专属片单。",
    keywords: "我的收藏,收藏影片,免费看大片",
  });

  const movies = favorites
    .map((id) => getMovieById(id))
    .filter((movie): movie is Movie => Boolean(movie));

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-10">
      <nav aria-label="面包屑导航" className="flex items-center gap-1.5 text-xs text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-300">
          首页
        </Link>
        <i className="ri-arrow-right-s-line text-foreground-600" />
        <span className="text-foreground-200">我的收藏</span>
      </nav>

      <div className="mt-4 rounded-lg border border-background-200/70 bg-gradient-to-r from-primary-950/60 via-background-100 to-background-100 p-5 md:p-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-accent-400">
              <span className="h-3 w-0.5 rounded-full bg-accent-500" />
              My list
            </p>
            <h1 className="mt-2 flex items-center gap-3 font-heading text-2xl font-semibold text-foreground-50 md:text-3xl">
              <i className="ri-heart-3-fill text-accent-400" />
              我的收藏
            </h1>
            <p className="mt-2 text-sm text-foreground-400">
              收藏的影片会保存在当前浏览器中，清除浏览器数据后将无法恢复。
            </p>
          </div>
          <p className="text-sm text-foreground-400">
            已收藏
            <span className="mx-1 font-heading text-lg font-semibold text-accent-400">
              {movies.length}
            </span>
            部影片
          </p>
        </div>
      </div>

      <div className="mt-6">
        {movies.length > 0 ? (
          <MovieGrid movies={movies} showSynopsis />
        ) : (
          <>
            <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100/50 px-6 py-14 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200/70 text-foreground-400">
                <i className="ri-heart-3-line text-2xl" />
              </span>
              <h2 className="mt-4 font-heading text-lg font-medium text-foreground-100">
                还没有收藏任何影片
              </h2>
              <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-500">
                在影片卡片或详情页点击收藏按钮，就能把想看的影片收进这里，下次打开直接继续。
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-3">
                <Link
                  to="/category/全部"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-film-line text-base" />
                  去挑几部影片
                </Link>
                <Link
                  to="/category/全部?sort=hot"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-200 transition-colors hover:border-primary-500/60 hover:text-primary-300"
                >
                  看看热门榜单
                </Link>
              </div>
            </div>

            <section className="mt-12">
              <SectionHeading
                eyebrow="Popular now"
                title="从这些影片开始收藏"
                description="近期最受欢迎的影片，点开详情页即可加入收藏。"
              />
              <MovieGrid movies={getHotMovies(5)} />
            </section>
          </>
        )}
      </div>
    </div>
  );
}